import { StaticProvider, Type } from '@angular/core';
import { Store } from '@datorama/akita';
import { ErrorHandlerService, SentryService, Y42ErrorTag } from '@y42/error-handling';
import { Y42FeatureType } from '@y42/json-schema';
import { AbstractFeatureAclService } from '../acl/abstract-feature-acl.service';
import { DetailTabModel, DETAIL_TABS } from '../detail-view';
import { AbstractFeatureGitService, getY42GitPath, GIT_FOLDER, Y42GitFolder } from '../git';
import { Y42FeatureToGitFolder } from '../git/feature-type-to-git-folder.model';
import { AbstractFeatureEntityState } from '../models/abstract-feature-entity-state.model';
import { AbstractGitEntityState } from '../models/abstract-git-entity-state.model';
import { UserRelatedState } from '../models/user-related.model';
import { Y42FeatureTypeToRoute, Y42Route, Y42_ROUTE } from '../routing';
import { AbstractFeatureChildQueryEntity } from '../state/abstract-feature-child-query-entity.service';
import { AbstractFeatureEntityStore } from '../state/abstract-feature-entity-store.service';
import { AbstractFeatureQueryEntity } from '../state/abstract-feature-query-entity.service';
import { AbstractFeatureService } from './abstract-feature.service';
import { FEATURE_TYPE } from './feature-type.injection-token';

export function provideFeatureType(featureType: Y42FeatureType) {
  return function (errorHandler: ErrorHandlerService) {
    errorHandler.setTag(Y42ErrorTag.FeatureType, featureType);
    return featureType;
  };
}

export function provideFeatureGitFolder(featureType: Y42FeatureType) {
  return Y42FeatureToGitFolder[featureType];
}

export function provideFeatureGitRootPath(gitFolder: Y42GitFolder) {
  return getY42GitPath(gitFolder);
}

export function provideFeatureRoute(featureType: Y42FeatureType) {
  return Y42FeatureTypeToRoute[featureType];
}

export interface FeatureServiceDef {
  FEATURE_TYPE: Y42FeatureType;
  GIT_FOLDER: Y42GitFolder;
  FEATURE_ROUTE: Y42Route;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  AbstractFeatureService: Type<AbstractFeatureService<AbstractFeatureEntityState<any, any> & UserRelatedState>>;
  AbstractFeatureEntityStore: Type<Store>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  AbstractFeatureQueryEntity: Type<AbstractFeatureQueryEntity<AbstractFeatureEntityState<any, any>, any>>;
  AbstractFeatureGitService: Type<AbstractFeatureGitService>;
  AbstractFeatureAclService: Type<AbstractFeatureAclService<Y42FeatureType>>;
  DETAIL_TABS: DetailTabModel[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  AbstractFeatureChildQueryEntity: Type<AbstractFeatureChildQueryEntity<AbstractGitEntityState<any, any>, any>> | null;
}

export function provideFeature(providers: FeatureServiceDef): StaticProvider[] {
  const {
    AbstractFeatureEntityStore: store,
    AbstractFeatureQueryEntity: query,
    AbstractFeatureService: service,
    AbstractFeatureGitService: gitService,
    AbstractFeatureAclService: aclService,
    AbstractFeatureChildQueryEntity: childQuery,
    FEATURE_TYPE: featureType,
    DETAIL_TABS: detailTabs,
  } = providers;
  const providersDef: StaticProvider[] = [
    {
      provide: FEATURE_TYPE,
      useFactory: provideFeatureType(featureType),
      deps: [SentryService],
    },
    {
      provide: GIT_FOLDER,
      useFactory: provideFeatureGitFolder,
      deps: [FEATURE_TYPE],
    },
    {
      provide: Y42_ROUTE,
      useFactory: provideFeatureRoute,
      deps: [FEATURE_TYPE],
    },
    {
      provide: AbstractFeatureEntityStore,
      useExisting: store,
    },
    {
      provide: AbstractFeatureQueryEntity,
      useExisting: query,
    },
    {
      provide: AbstractFeatureService,
      useExisting: service,
    },
    {
      provide: AbstractFeatureAclService,
      useExisting: aclService,
    },
    {
      provide: AbstractFeatureGitService,
      useExisting: gitService,
    },
    {
      provide: DETAIL_TABS,
      useValue: detailTabs,
    },
  ];

  if (childQuery) {
    providersDef.push({
      provide: AbstractFeatureChildQueryEntity,
      useExisting: childQuery,
    });
  }

  return providersDef;
}
